// core/routeSetup.js
// Configure routes and connect router to skill system

import { router } from './router.js';
import { skillRegistry } from './skillRegistry.js';
import { loadSkill, hasSkillModule } from './skillLoader.js';
import { loadSkillCSS } from './cssLoader.js';
import { gameState } from '../game/gameState.js';
import { IS_DEV } from '../config/env.js';

/** Currently active skill ID */
let currentSkillId = null;

/** Skill ID aliases for renamed skills (old -> new) */
const SKILL_ALIASES = {
    'shaping': 'architecting',
};

/**
 * Update sidebar active state
 * @param {string} skillId - Active skill ID
 */
function updateSidebarActive(skillId) {
    // Clear all active states
    document.querySelectorAll('.skill-item').forEach(el => {
        el.classList.remove('active');
    });

    // Set active state on current skill
    const activeItem = document.querySelector(`[data-skill="${skillId}"]`);
    if (activeItem) {
        activeItem.classList.add('active');
    }
}

/**
 * Update skill levels in sidebar
 */
function updateSidebarLevels() {
    const skills = skillRegistry.getAll();
    for (const skill of skills) {
        const skillData = gameState.getSkill(skill.id);
        if (!skillData || skill.id === 'admin') continue;

        const levelEl = document.querySelector(`[data-skill="${skill.id}"] .skill-level`);
        if (levelEl) {
            const virtualDisplay = skillData.virtualLevel > 99 ? ` (${skillData.virtualLevel})` : '';
            levelEl.textContent = `Level ${skillData.level}/99${virtualDisplay}`;
        }
    }
}

/**
 * Render a skill page
 * @param {string} skillId - Skill to render
 * @param {string} [tab] - Optional tab to render
 */
async function renderSkill(skillId, tab = null) {
    // Check for skill aliases (renamed skills)
    const resolvedId = SKILL_ALIASES[skillId] || skillId;
    if (resolvedId !== skillId) {
        // Redirect to new skill ID
        router.navigate(`#/${resolvedId}${tab ? '/' + tab : ''}`, true);
        return;
    }

    const skillConfig = skillRegistry.get(skillId);
    if (!skillConfig) {
        console.error(`[RouteSetup] Skill not found: ${skillId}`);
        router.navigate('#/', true);
        return;
    }

    // Check dev-only restriction
    if (skillConfig.devOnly && !IS_DEV) {
        console.warn(`[RouteSetup] Skill '${skillId}' is dev-only`);
        router.navigate('#/', true);
        return;
    }

    // Cleanup previous skill if different
    if (currentSkillId && currentSkillId !== skillId) {
        await skillRegistry.cleanupSkill(currentSkillId);
    }

    currentSkillId = skillId;

    // Load skill CSS if specified
    if (skillConfig.css) {
        await loadSkillCSS(skillId, skillConfig.css);
    }

    // Initialize skill if not already done
    if (!skillRegistry.isInitialized(skillId)) {
        await skillRegistry.initSkill(skillId);
    }

    // Update sidebar
    updateSidebarActive(skillId);
    updateSidebarLevels();

    // Build render params
    const params = { tab };

    // If skill has tabs but no tab specified, redirect to default tab
    if (skillRegistry.hasTabs(skillId) && !tab) {
        const defaultTab = skillRegistry.getDefaultTab(skillId);
        if (defaultTab) {
            router.navigate(`#/${skillId}/${defaultTab}`, true);
            return;
        }
    }

    // Validate tab if specified
    if (tab && !skillRegistry.hasTab(skillId, tab)) {
        console.warn(`[RouteSetup] Tab '${tab}' not found for skill '${skillId}'`);
        const defaultTab = skillRegistry.getDefaultTab(skillId);
        if (defaultTab) {
            router.navigate(`#/${skillId}/${defaultTab}`, true);
            return;
        }
    }

    // Render the skill
    try {
        await skillConfig.render(params);
    } catch (error) {
        console.error(`[RouteSetup] Error rendering skill '${skillId}':`, error);
    }
}

/**
 * Load and render a skill (handles loading if not yet loaded)
 * @param {string} skillId
 * @param {string} [tab]
 */
async function loadAndRenderSkill(skillId, tab = null) {
    // Check if skill has a module to load
    if (hasSkillModule(skillId) && !skillRegistry.has(skillId)) {
        const loaded = await loadSkill(skillId);
        if (!loaded) {
            console.error(`[RouteSetup] Failed to load skill: ${skillId}`);
            router.navigate('#/', true);
            return;
        }
    }

    await renderSkill(skillId, tab);
}

/**
 * Render welcome/home screen
 */
function renderWelcome() {
    const container = document.getElementById('skill-content');
    container.innerHTML = `
        <div class="welcome-screen">
            <h2>Welcome to IdleRealms</h2>
            <p>Select a skill from the sidebar to begin your adventure.</p>
        </div>
    `;
    updateSidebarActive(null);
}

/**
 * Setup all routes
 */
export function setupRoutes() {
    // Home/welcome route
    router.register('#/', () => {
        // Redirect to last skill or default
        const lastSkill = localStorage.getItem('idleRealms_lastSkill') || 'mining';
        router.navigate(`#/${lastSkill}`, true);
    });

    // Main skill route (no tab)
    router.register('#/:skill', async ({ skill }) => {
        await loadAndRenderSkill(skill);
    });

    // Skill with tab
    router.register('#/:skill/:tab', async ({ skill, tab }) => {
        await loadAndRenderSkill(skill, tab);
    });

    // Save last viewed skill after navigation
    router.afterEach((result) => {
        if (result?.params?.skill) {
            localStorage.setItem('idleRealms_lastSkill', result.params.skill);
        }
    });
}

/**
 * Get the currently active skill ID
 * @returns {string|null}
 */
export function getCurrentSkillId() {
    return currentSkillId;
}

/**
 * Get the currently active skill config
 * @returns {Object|null}
 */
export function getCurrentSkill() {
    return currentSkillId ? skillRegistry.get(currentSkillId) : null;
}

/**
 * Force re-render of current skill
 */
export async function refreshCurrentSkill() {
    if (!currentSkillId) return;

    const { params } = router.getCurrentRoute();
    await renderSkill(currentSkillId, params.tab);
}

/**
 * Navigate to a skill
 * @param {string} skillId
 * @param {string} [tab]
 */
export function navigateToSkill(skillId, tab = null) {
    if (tab) {
        router.navigate(`#/${skillId}/${tab}`);
    } else {
        router.navigate(`#/${skillId}`);
    }
}

export default {
    setupRoutes,
    getCurrentSkillId,
    getCurrentSkill,
    refreshCurrentSkill,
    navigateToSkill,
};
